import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, Github } from 'lucide-react';

const Software: React.FC = () => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto"
    >
      {/* Header Section */}
      <div className="text-center mb-12">
        <motion.h1 
          className="font-display text-5xl md:text-7xl font-semibold text-stone-800 mb-6 leading-tight pb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {t('software.title')}
        </motion.h1>
        <motion.p 
          className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {t('software.subtitle')}
        </motion.p>
      </div>

      {/* GDDS */}
      <motion.div
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.55 }}
      >
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-stone-200/70 hover:shadow-2xl transition-all duration-300 group">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0">
              <div className="w-full md:w-80 h-48 md:h-60 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 bg-[#0f1218]">
                <video
                  className="w-full h-full object-contain"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                >
                  <source src="/files/publications/gdds/videos/gdds_forward_noising_teaser.mp4" type="video/mp4" />
                  Your browser does not support the teaser video.
                </video>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h2 className="font-display text-3xl font-semibold text-stone-800 mb-3 group-hover:text-[#5f6d52] transition-colors duration-300">
                    {t('software.gdds.title')}
                  </h2>
                  <div className="flex items-center mb-4">
                    <span className="text-sm font-semibold text-[#5f6d52] bg-[#eef2e9] px-3 py-1 rounded-full">
                      {t('software.tags.machineLearning')}
                    </span>
                    <span className="text-sm font-semibold text-[#7b7368] bg-[#f3ede6] px-3 py-1 rounded-full ml-2">
                      {t('software.tags.python')}
                    </span>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="w-4 h-4 bg-[#6f7f5f] rounded-full animate-pulse"></div>
                </div>
              </div>

              <p className="text-stone-700 mb-6 leading-relaxed">
                {t('software.gdds.description')}
              </p>

              <div className="flex flex-wrap gap-3">
                <a href="https://github.com/ozekri/gdds" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center px-4 py-2 bg-[#5f6d52] text-white font-medium rounded-lg hover:bg-[#49543f] transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg">
                  <Github className="w-5 h-5 mr-2" />
                  {t('software.gdds.viewOnGithub')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* SEPO */}
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.7 }}
      >
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-stone-200/70 hover:shadow-2xl transition-all duration-300 group">
          <div className="flex flex-col md:flex-row gap-6">
            {/* SEPO GIF */}
            <div className="flex-shrink-0">
              <div className="w-full md:w-80 h-48 md:h-60 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300">
                <img 
                  src="/files/publications/gifs_and_imgs/sepo.gif" 
                  alt="SEPO: Fine-Tuning Discrete Diffusion Models with Policy Gradient Methods" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h2 className="font-display text-3xl font-semibold text-stone-800 mb-3 group-hover:text-[#5f6d52] transition-colors duration-300">
                    {t('software.sepo.title')}
                  </h2>
                  <div className="flex items-center mb-4">
                    <span className="text-sm font-semibold text-[#5f6d52] bg-[#eef2e9] px-3 py-1 rounded-full">
                      {t('software.tags.machineLearning')}
                    </span>
                    <span className="text-sm font-semibold text-[#7b7368] bg-[#f3ede6] px-3 py-1 rounded-full ml-2">
                      {t('software.tags.python')}
                    </span>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="w-4 h-4 bg-[#6f7f5f] rounded-full animate-pulse"></div>
                </div>
              </div>
              
              <p className="text-stone-700 mb-6 leading-relaxed">
                {t('software.sepo.description')}
              </p>
              
              <div className="flex flex-wrap gap-3">
                <a href="https://github.com/ozekri/SEPO" target="_blank" rel="noopener noreferrer" 
                   className="inline-flex items-center px-4 py-2 bg-[#5f6d52] text-white font-medium rounded-lg hover:bg-[#49543f] transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg">
                  <Github className="w-5 h-5 mr-2" />
                  {t('software.sepo.viewOnGithub')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CKN from Scratch */}
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-stone-200/70 hover:shadow-2xl transition-all duration-300 group">
          <div className="flex flex-col md:flex-row gap-6">
            {/* CKN Image */}
            <div className="flex-shrink-0">
              <div className="w-full md:w-80 h-48 md:h-60 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300">
                <img 
                  src="/files/software/CKN.png" 
                  alt="CKN from Scratch" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h2 className="font-display text-3xl font-semibold text-stone-800 mb-3 group-hover:text-[#5f6d52] transition-colors duration-300">
                    {t('software.ckn.title')}
                  </h2>
                  <div className="flex items-center mb-4">
                    <span className="text-sm font-semibold text-[#5f6d52] bg-[#eef2e9] px-3 py-1 rounded-full">
                      {t('software.tags.machineLearning')}
                    </span>
                    <span className="text-sm font-semibold text-[#7b7368] bg-[#f3ede6] px-3 py-1 rounded-full ml-2">
                      {t('software.tags.python')}
                    </span>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="w-4 h-4 bg-[#6f7f5f] rounded-full animate-pulse"></div>
                </div>
              </div>
              
              <p className="text-stone-700 mb-6 leading-relaxed">
                {t('software.ckn.description')}{' '}
                <a href="https://proceedings.neurips.cc/paper_files/paper/2016/file/fc8001f834f6a5f0561080d134d53d29-Paper.pdf" target="_blank" rel="noopener noreferrer" 
                   className="text-[#5f6d52] hover:text-[#49543f] underline decoration-[#a9b49f] decoration-1 underline-offset-4 transition-all duration-300">
                  Mairal et al. (2016)
                </a>. {t('software.ckn.description2')}
              </p>
              
              <div className="flex flex-wrap gap-3">
                <a href="https://github.com/ozekri/CKN_from_Scratch" target="_blank" rel="noopener noreferrer" 
                   className="inline-flex items-center px-4 py-2 bg-[#5f6d52] text-white font-medium rounded-lg hover:bg-[#49543f] transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg">
                  <Github className="w-5 h-5 mr-2" />
                  {t('software.ckn.viewOnGithub')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.div>



      {/* Collaboration Section */}
      <motion.div 
        className="mt-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <div className="rounded-[1.75rem] border border-stone-200/80 bg-[linear-gradient(135deg,rgba(250,248,244,0.95),rgba(243,239,231,0.92))] p-8 shadow-[0_20px_60px_rgba(31,41,55,0.08)]">
          <h2 className="font-display text-3xl font-semibold text-stone-800 mb-6 flex items-center">
            <span className="w-8 h-px bg-stone-500 mr-4"></span>
            {t('software.collaborate.title')}
          </h2>
          <p className="text-gray-700 mb-4">
            {t('software.collaborate.description')}
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="https://github.com/ozekri" target="_blank" rel="noopener noreferrer" 
               className="inline-flex items-center px-4 py-2 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-900 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg">
              <Github className="w-5 h-5 mr-2" />
              {t('software.collaborate.githubProfile')}
            </a>
            <a href="mailto:oussama.zekri@ens-paris-saclay.fr" 
               className="inline-flex items-center px-4 py-2 bg-[#5f6d52] text-white font-medium rounded-lg hover:bg-[#49543f] transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg">
              <Mail className="w-5 h-5 mr-2" />
              {t('software.collaborate.contactMe')}
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Software; 
